import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, PackagePlus, ShoppingCart, Search } from 'lucide-react';

const CircularMenu = ({ onAdd, onWizard, onMarket, onSearch }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Menü öğeleri ayarları
    // 3 Item: Market(180-210), Giriş(210-240), Toplu(240-270)
    const items = [
        {
            id: 'market',
            label: 'Market',
            icon: <ShoppingCart size={22} />,
            action: onMarket,
            color: '#e11d48', // Rose 600
            startAngle: 180,
            endAngle: 210
        },
        {
            id: 'in',
            label: 'Giriş',
            icon: <Plus size={22} />,
            action: onAdd,
            color: '#dc2626',
            startAngle: 210,
            endAngle: 240
        },
        {
            id: 'bulk',
            label: 'Toplu',
            icon: <PackagePlus size={22} />,
            action: onWizard,
            color: '#1e293b',
            startAngle: 240,
            endAngle: 270
        }
    ];

    const size = 260;
    const center = size;
    const outerRadius = 220;
    const innerRadius = 75;
    const gapAngle = 1.0;

    const containerVariants = {
        hidden: {
            opacity: 0,
            scale: 0.2, // Küçülerek başla
            transition: { when: "afterChildren" }
        },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 260,
                damping: 25,
                // Stagger yerine manuel delay kullanacağız
            }
        },
        exit: {
            opacity: 0,
            scale: 0, // Tüm blok yok ol
            transition: {
                duration: 0.3,
                ease: "backIn",
                delay: 0.35 // Kolların toplanmasını bekle (Out: 0s + In: 0.15s + Buffer)
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, scale: 0 },
        visible: (i) => ({
            opacity: 1,
            scale: 1,
            rotate: 0,
            transition: {
                // Opening delay: Index 3 (Toplu) opens first, then others with slight lag
                delay: i === 3 ? 0 : (3 - i) * 0.1,
                type: "spring",
                stiffness: 300,
                damping: 25
            }
        }),
        exit: (i) => ({
            opacity: 1,
            scale: 1,
            // Return to center (relative rotation to close)
            rotate: i === 2 ? 30 : i === 1 ? 60 : i === 0 ? 90 : 0,
            transition: {
                delay: i !== 3 ? 0 : 0.15,
                duration: 0.25,
                ease: "easeInOut"
            }
        })
    };

    const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
        const angleInRadians = (angleInDegrees) * Math.PI / 180.0;
        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians))
        };
    };

    const describeRingSegment = (x, y, innerR, outerR, startAngle, endAngle) => {
        const s = startAngle + gapAngle;
        const e = endAngle - gapAngle;

        const outerStart = polarToCartesian(x, y, outerR, e);
        const outerEnd = polarToCartesian(x, y, outerR, s);
        const innerStart = polarToCartesian(x, y, innerR, e);
        const innerEnd = polarToCartesian(x, y, innerR, s);

        const largeArcFlag = e - s <= 180 ? "0" : "1";

        return [
            "M", outerStart.x, outerStart.y,
            "A", outerR, outerR, 0, largeArcFlag, 0, outerEnd.x, outerEnd.y,
            "L", innerEnd.x, innerEnd.y,
            "A", innerR, innerR, 0, largeArcFlag, 1, innerStart.x, innerStart.y,
            "Z"
        ].join(" ");
    };

    return (
        <div className="fixed bottom-0 right-0 z-[160] pointer-events-none p-8 w-[400px] h-[400px] flex items-end justify-end overflow-visible">

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                        className="fixed inset-0 bg-black/45 backdrop-blur-[3px] z-[-1] pointer-events-auto"
                    />
                )}
            </AnimatePresence>

            <div className="relative w-14 h-14 pointer-events-none">

                <div className="absolute bottom-7 right-7 w-[300px] h-[300px] pointer-events-none">
                    <svg
                        viewBox={`0 0 ${size} ${size}`}
                        className="w-full h-full overflow-visible"
                    >
                        <AnimatePresence>
                            {isOpen && (
                                <motion.g
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                >
                                    {items.map((item, index) => {
                                        const midAngle = (item.startAngle + item.endAngle) / 2;
                                        const midRadius = (innerRadius + outerRadius) / 2;

                                        const textPos = polarToCartesian(center, center, midRadius + 30, midAngle);
                                        const iconPos = polarToCartesian(center, center, midRadius - 8, midAngle);

                                        return (
                                            <motion.g
                                                key={item.id}
                                                custom={index}
                                                variants={itemVariants}
                                                style={{ originX: '100%', originY: '100%' }}
                                                className="pointer-events-auto cursor-pointer group outline-none"
                                                onClick={() => {
                                                    item.action();
                                                    setIsOpen(false);
                                                }}
                                            >
                                                <path
                                                    d={describeRingSegment(center, center, innerRadius, outerRadius, item.startAngle, item.endAngle)}
                                                    fill={item.color}
                                                    className="hover:brightness-110 transition-all duration-300 drop-shadow-2xl stroke-white/10 stroke-1"
                                                />

                                                <foreignObject
                                                    x={iconPos.x - 15}
                                                    y={iconPos.y - 15}
                                                    width="30"
                                                    height="30"
                                                    className="pointer-events-none"
                                                >
                                                    <div className="flex items-center justify-center text-white drop-shadow-lg">
                                                        {item.icon}
                                                    </div>
                                                </foreignObject>

                                                <text
                                                    x={textPos.x}
                                                    y={textPos.y}
                                                    fill="white"
                                                    textAnchor="middle"
                                                    alignmentBaseline="middle"
                                                    fontSize="10"
                                                    fontWeight="900"
                                                    className="uppercase tracking-[0.1em] pointer-events-none select-none drop-shadow-md"
                                                    transform={`rotate(${midAngle + 90}, ${textPos.x}, ${textPos.y})`}
                                                >
                                                    {item.label}
                                                </text>
                                            </motion.g>
                                        );
                                    })}
                                </motion.g>
                            )}
                        </AnimatePresence>
                    </svg>
                </div>

                {/* Akıllı Arama Butonu - Üstte Ayrı Durur */}
                <motion.button
                    initial={{ opacity: 0, y: 10, scale: 0.8 }}
                    animate={{ opacity: 1, y: -70, scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onSearch}
                    className="absolute z-[161] size-12 rounded-full shadow-xl bg-blue-600 border-4 border-white dark:border-slate-800 flex items-center justify-center text-white pointer-events-auto"
                >
                    <Search size={24} />
                    <div className="absolute -top-8 bg-blue-600 text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap text-white shadow-lg animate-bounce">HEMEN TARİF AL!</div>
                </motion.button>

                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`z-[161] size-14 rounded-full shadow-2xl transition-all duration-500 flex items-center justify-center pointer-events-auto focus:outline-none border-4 border-white dark:border-slate-800 ${isOpen ? 'bg-slate-900 scale-110' : 'bg-red-600 hover:scale-105'
                        } text-white`}
                >
                    {isOpen ? <Minus size={32} /> : <Plus size={32} />}
                </button>
            </div>
        </div>
    );
};

export default CircularMenu;
